import { useTranslation } from "react-i18next"
import { useQuery } from "@tanstack/react-query"
import { Badge, Skeleton } from "@nexus/ui"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { personaOnboardingSuggestionsQueryOptions } from "@/lib/queries"
import {
  PersonasOnboardingIntro,
  type PersonasOnboardingIntroProps,
} from "./PersonasOnboardingIntro"

const INTRO_AVATAR_SKELETON_COUNT = 5

export type PersonasOnboardingIntroViewProps = Omit<PersonasOnboardingIntroProps, "personas"> & {
  /** When false, suggestions are not fetched (no permission). */
  canLoadSuggestions?: boolean
}

/** Setup chrome + Figma intro — loads suggested personas for the avatar stack. */
export function PersonasOnboardingIntroView({
  canLoadSuggestions = true,
  ...introProps
}: PersonasOnboardingIntroViewProps) {
  const { t } = useTranslation()
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const suggestionsQuery = useQuery(
    personaOnboardingSuggestionsQueryOptions(activeTenantId, canLoadSuggestions),
  )
  const personas = suggestionsQuery.data?.suggested ?? []

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="personas-onboarding-intro-view">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            {t("personas.title", "User Personas")}
          </h1>
          <Badge variant="status-delivered" dot={false} iconStart="plus">
            {t("personas.onboarding.badge", "Setup")}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {t("personas.onboarding.header.introSubtitle", "A guided setup, then your full persona library.")}
        </p>
      </div>

      {suggestionsQuery.isLoading ? (
        <section className="mx-auto flex w-full max-w-3xl flex-col items-center px-2 py-6">
          <div className="flex w-full flex-col items-center gap-7 rounded-md border border-dashed border-border bg-card px-6 py-10 sm:px-10 sm:py-[60px]">
            <div className="flex -space-x-3.5" aria-hidden>
              {Array.from({ length: INTRO_AVATAR_SKELETON_COUNT }).map((_, i) => (
                <Skeleton key={i} className="size-11 rounded-full border-2 border-card sm:size-12" />
              ))}
            </div>
            <Skeleton className="h-7 w-72 rounded-md" />
            <Skeleton className="h-4 w-full max-w-xl rounded-md" />
          </div>
        </section>
      ) : (
        <PersonasOnboardingIntro {...introProps} personas={personas} />
      )}
    </div>
  )
}
